import React, { useState, useEffect } from 'react';
import { SunIcon, MoonIcon, PlusIcon, Bars3Icon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import ChatComponent from './components/ChatComponent';
import Sidebar from './components/Sidebar';
import WelcomeModal from './components/WelcomeModal';
import LoginComponent from './components/LoginComponent';
import { trackEvent, trackException, trackPageView, setAuthenticatedUserContext, clearAuthenticatedUserContext } from './services/appInsights';

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : true;
  });

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    return !hasSeenWelcome;
  });
  
  // Conversation management
  const [conversations, setConversations] = useState(() => {
    const saved = localStorage.getItem('conversations');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [currentConversationId, setCurrentConversationId] = useState(() => {
    const saved = localStorage.getItem('currentConversationId');
    return saved || null;
  });

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;

    try {
      // Check if token is expired (basic client-side check)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;
      return payload.exp > now;
    } catch {
      return false;
    }
  });
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Load current conversation messages
  useEffect(() => {
    if (currentConversationId) {
      const conversation = conversations.find(c => c.id === currentConversationId);
      if (conversation) {
        setMessages(conversation.messages);
      }
    } else {
      setMessages([]);
    }
  }, [currentConversationId, conversations]);

  // Save conversations to localStorage
  useEffect(() => {
    localStorage.setItem('conversations', JSON.stringify(conversations));
  }, [conversations]);

  // Save current conversation ID
  useEffect(() => {
    if (currentConversationId) {
      localStorage.setItem('currentConversationId', currentConversationId);
    }
  }, [currentConversationId]);

  // Dark mode effect
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Backend wake-up call - runs once on first load
  // Ensures backend container scales up from 0 replicas before user needs it
  useEffect(() => {
    const wakeBackend = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://everfield-gpt-v2-backend.politecoast-05366af5.swedencentral.azurecontainerapps.io';
        await fetch(`${apiUrl}/health`, {
          method: 'GET',
          signal: AbortSignal.timeout(45000), // 45s timeout for cold start
        });
      } catch (error) {
        // Silently ignore - backend warming up or network issues don't affect UX
      }
    };

    wakeBackend();
  }, []); // Empty deps = runs once on mount

  const generateConversationTitle = async (firstMessage) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://everfield-gpt-v2-backend.politecoast-05366af5.swedencentral.azurecontainerapps.io';
      const response = await fetch(`${apiUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          messages: [{
            role: 'user',
            content: `Create a concise, descriptive title (maximum 8 words) for a conversation that starts with this message: "${firstMessage}". Only respond with the title, nothing else.`
          }]
        }),
      });

      const data = await response.json();

      if (response.ok && data.response) {
        // Clean up the response and ensure it's not too long
        let title = data.response.trim().replace(/^["']|["']$/g, ''); // Remove quotes
        const words = title.split(' ');
        if (words.length > 8) {
          title = words.slice(0, 8).join(' ') + '...';
        }
        return title;
      }
    } catch (error) {
      console.error('Error generating AI title:', error);
    }

    // Fallback to simple method if AI fails
    const words = firstMessage.split(' ').slice(0, 6);
    return words.join(' ') + (firstMessage.split(' ').length > 6 ? '...' : '');
  };

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
    localStorage.setItem('hasSeenWelcome', 'true');
  };

  const handleLogin = async (azureAccessToken) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://everfield-gpt-v2-backend.politecoast-05366af5.swedencentral.azurecontainerapps.io';
      const response = await fetch(`${apiUrl}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: azureAccessToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      // Store token and update auth state
      localStorage.setItem('accessToken', data.access_token);
      setIsAuthenticated(true);

      // Track successful login
      try {
        const payload = JSON.parse(atob(data.access_token.split('.')[1]));
        setAuthenticatedUserContext(payload.email || payload.sub);
        trackEvent('UserLogin', { email: payload.email, name: payload.name });
      } catch {
        // If token parsing fails, just track basic login
        trackEvent('UserLogin');
      }

      // Clear any existing error states
      setError(null);
      setAuthError(null);

    } catch (error) {
      console.error('Login error:', error);
      setAuthError(error.message);
      localStorage.removeItem('accessToken');
      setIsAuthenticated(false);

      // Track login failure
      trackException(error, { context: 'login', errorMessage: error.message });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    // Track logout event
    trackEvent('UserLogout');
    clearAuthenticatedUserContext();

    localStorage.removeItem('accessToken');
    setIsAuthenticated(false);
    setCurrentConversationId(null);
    setMessages([]);
    setError(null);
    setAuthError(null);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const handleSendMessage = async (messageContent) => {
    const startTime = Date.now();
    const userMessage = { role: 'user', content: messageContent, timestamp: Date.now() };
    let currentMessages;
    let conversationId;
    let isNewConversation = false;

    // If no current conversation, create a new one
    if (!currentConversationId) {
      isNewConversation = true;
      conversationId = Date.now().toString();
      currentMessages = [userMessage];

      // Create conversation with temporary title first
      const words = messageContent.split(' ').slice(0, 6);
      const tempTitle = words.join(' ') + (messageContent.split(' ').length > 6 ? '...' : '');

      const newConversation = {
        id: conversationId,
        name: tempTitle,
        aiTitle: null, // Will be generated after first response
        messages: currentMessages,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversationId(conversationId);
      setMessages(currentMessages);

      // Track new conversation creation
      trackEvent('ConversationCreated', {
        conversationId: conversationId,
        messageLength: messageContent.length
      });

      // Generate AI title in background
      generateConversationTitle(messageContent).then(aiTitle => {
        setConversations(prev => prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, aiTitle, updatedAt: Date.now() }
            : conv
        ));
      });
    } else {
      // Add to existing conversation
      conversationId = currentConversationId;
      currentMessages = [...messages, userMessage];
      setMessages(currentMessages);

      setConversations(prev => prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, messages: currentMessages, updatedAt: Date.now() }
          : conv
      ));
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://everfield-gpt-v2-backend.politecoast-05366af5.swedencentral.azurecontainerapps.io';
      const response = await fetch(`${apiUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          messages: currentMessages
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          // Token expired or invalid - force logout
          handleLogout();
          throw new Error("Your session has expired. Please log in again.");
        }

        // Handle different types of errors more gracefully
        let userFriendlyError;
        if (response.status === 429) {
          userFriendlyError = "I'm currently experiencing high demand. Please wait a moment and try again.";
        } else if (response.status >= 500) {
          userFriendlyError = data.detail || "I'm experiencing technical difficulties. Please try again in a few moments.";
        } else if (response.status === 400) {
          userFriendlyError = "There was an issue with your message. Please try rephrasing it.";
        } else {
          userFriendlyError = data.detail || 'An unexpected error occurred. Please try again.';
        }
        throw new Error(userFriendlyError);
      }

      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: Date.now(),
        modelUsed: data.model_used
      };
      const finalMessages = [...currentMessages, assistantMessage];

      setMessages(finalMessages);

      // Update conversation with assistant response
      setConversations(prev => prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, messages: finalMessages, updatedAt: Date.now() }
          : conv
      ));

      // Track successful message
      const responseTime = Date.now() - startTime;
      trackEvent('MessageSent', {
        conversationId: conversationId,
        isNewConversation: isNewConversation,
        messageLength: messageContent.length,
        responseLength: data.response.length,
        responseTimeMs: responseTime,
        modelUsed: data.model_used
      });

    } catch (error) {
      console.error('Error:', error);

      // Track error
      trackException(error, {
        context: 'sendMessage',
        conversationId: conversationId,
        messageLength: messageContent.length,
        errorMessage: error.message
      });

      // Enhanced error display with retry suggestion
      let errorMessage = error.message;

      // Add helpful context based on error type
      if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = "I couldn't connect to my service. Please check your internet connection and try again.";
      } else if (error.message.includes('timeout')) {
        errorMessage = "The request took too long. Please try with a shorter message.";
      }

      setError(errorMessage);

      // Remove user message on error
      const errorMessages = currentMessages.slice(0, -1);
      setMessages(errorMessages);
      setConversations(prev => prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, messages: errorMessages, updatedAt: Date.now() }
          : conv
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setError(null);
    setSidebarOpen(false);

    // Track new conversation button click
    trackEvent('NewConversationClicked');

    // Focus input after starting new conversation
    setTimeout(() => {
      const inputElement = document.querySelector('textarea[placeholder*="Type your message"]');
      if (inputElement) {
        inputElement.focus();
      }
    }, 100);
  };

  const handleSelectConversation = (conversationId) => {
    setCurrentConversationId(conversationId);
    setError(null);
    setSidebarOpen(false);

    // Track conversation selection
    trackEvent('ConversationSelected', { conversationId });
  };

  const handleRenameConversation = (conversationId, newName) => {
    setConversations(prev => prev.map(conv =>
      conv.id === conversationId
        ? { ...conv, name: newName, aiTitle: newName, updatedAt: Date.now() }
        : conv
    ));
  };

  const handleDuplicateConversation = (conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      const displayName = conversation.aiTitle || conversation.name;
      const newConversation = {
        ...conversation,
        id: Date.now().toString(),
        name: `${displayName} (Copy)`,
        aiTitle: `${displayName} (Copy)`,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      setConversations(prev => [newConversation, ...prev]);
    }
  };

  const handleDeleteConversation = (conversationId) => {
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    if (currentConversationId === conversationId) {
      setCurrentConversationId(null);
      setMessages([]);
    }

    // Track conversation deletion
    trackEvent('ConversationDeleted', { conversationId });
  };

  const handleClearHistory = () => {
    const conversationCount = conversations.length;

    setConversations([]);
    setCurrentConversationId(null);
    setMessages([]);
    setError(null);
    localStorage.removeItem('conversations');
    localStorage.removeItem('currentConversationId');

    // Track history clear
    trackEvent('HistoryCleared', { conversationCount });
  };

  // Show login component if not authenticated
  if (!isAuthenticated) {
    return (
      <LoginComponent
        onLogin={handleLogin}
        error={authError}
        isLoading={authLoading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50
                    dark:from-[#0f0f23] dark:via-[#16161e] dark:to-[#1a1b26]
                    animate-gradient-shift transition-all duration-500 relative">
      
      {/* Advanced Dark Mode Background Effects */}
      {darkMode && (
        <>
          {/* Mesh gradient overlay */}
          <div className="mesh-gradient fixed inset-0 pointer-events-none z-0" />
          
          {/* Floating particles */}
          <div className="dark-bg-particles">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
            <div className="particle particle-5"></div>
            <div className="particle particle-6"></div>
          </div>
          
          {/* Floating orbs */}
          <div className="dark-bg-particles">
            <div className="floating-orb orb-1"></div>
            <div className="floating-orb orb-2"></div>
            <div className="floating-orb orb-3"></div>
          </div>
          
          {/* Enhanced background overlay */}
          <div className="dark-bg-overlay" />
        </>
      )}
      
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        conversations={conversations}
        currentConversationId={currentConversationId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onRenameConversation={handleRenameConversation}
        onDuplicateConversation={handleDuplicateConversation}
        onDeleteConversation={handleDeleteConversation}
        onClearHistory={handleClearHistory}
        darkMode={darkMode}
        onToggleDarkMode={() => setDarkMode(!darkMode)}
        onLogout={handleLogout}
      />

      {/* Header - Fixed at top */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-white/95 dark:bg-[#1a1b26]/95 backdrop-blur-xl
                     border-b border-gray-200/50 dark:border-[#414868]/50 shadow-lg
                     transition-all duration-300">
        <div className={`flex items-center justify-between p-4 ${sidebarOpen ? 'lg:ml-80' : ''}`}>
          <div className="flex items-center gap-4">
            {/* Sidebar toggle button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-xl bg-gray-100 dark:bg-[#414868]
                       hover:bg-gray-200 dark:hover:bg-[#565f89] transition-colors"
            >
              <Bars3Icon className="h-6 w-6 text-gray-700 dark:text-[#c0caf5]" />
            </button>

            <div className="flex items-center gap-3">
              <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                EverfieldGPT
              </h1>
              <span className="rounded-full border border-gray-300 bg-gray-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-gray-500
                           dark:border-[#414868] dark:bg-[#24283b] dark:text-[#9aa5ce]">
                Beta
              </span>
              <span className="hidden lg:inline-block border-l border-gray-300 pl-3 text-xs font-medium text-gray-500
                           dark:border-[#414868] dark:text-[#9aa5ce]">
                GPT-5.6 Sol
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* New Conversation Button */}
            <button
              onClick={handleNewConversation}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600
                       text-white rounded-xl hover:from-blue-600 hover:to-blue-700
                       transition-all duration-200 shadow-lg hover:shadow-xl modern-button font-medium"
            >
              <PlusIcon className="h-4 w-4" />
              <span>New Conversation</span>
            </button>

            {/* Logout Button - Desktop only */}
            <button
              onClick={handleLogout}
              className="hidden lg:flex h-10 px-4 items-center rounded-xl bg-red-100 dark:bg-red-900/30 hover:bg-red-200
                       dark:hover:bg-red-900/50 transition-all duration-200
                       shadow-lg hover:shadow-xl modern-button text-red-600 dark:text-red-400"
              title="Sign Out"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>

            {/* Dark Mode Toggle - Desktop only */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="hidden lg:flex h-10 px-4 items-center rounded-xl bg-gray-100 dark:bg-[#414868] hover:bg-gray-200
                       dark:hover:bg-[#565f89] transition-all duration-200
                       shadow-lg hover:shadow-xl modern-button"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? (
                <SunIcon className="h-5 w-5 text-[#e0af68]" />
              ) : (
                <MoonIcon className="h-5 w-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`pt-20 transition-all duration-300 ${sidebarOpen ? 'lg:ml-80' : ''}`}>
        {/* Chat Component */}
        <div className="flex-1 relative">
          <ChatComponent
            messages={messages}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            error={error}
            darkMode={darkMode}
          />
        </div>
      </div>

      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleCloseWelcomeModal}
      />
    </div>
  );
}

export default App;
